/** Per-key trailing debounce shared by class and function upload components. */
export interface TrailingDebounce {
  schedule(key: string, fn: () => void): void;
  flush(key?: string): void;
  cancel(key?: string): void;
  isPending(key: string): boolean;
}

export const ROTATE_SAVE_DEBOUNCE_MS = 700;

export function createTrailingDebounce(waitMs = ROTATE_SAVE_DEBOUNCE_MS): TrailingDebounce {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const pending = new Map<string, () => void>();

  const clearTimer = (key: string) => {
    const t = timers.get(key);
    if (t !== undefined) {
      clearTimeout(t);
      timers.delete(key);
    }
  };

  const schedule: TrailingDebounce["schedule"] = (key, fn) => {
    pending.set(key, fn);
    clearTimer(key);
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key);
        const run = pending.get(key);
        pending.delete(key);
        run?.();
      }, waitMs),
    );
  };

  const flush: TrailingDebounce["flush"] = (key) => {
    const keys = key !== undefined ? [key] : Array.from(pending.keys());
    for (const k of keys) {
      clearTimer(k);
      const run = pending.get(k);
      pending.delete(k);
      run?.();
    }
  };

  const cancel: TrailingDebounce["cancel"] = (key) => {
    const keys = key !== undefined ? [key] : Array.from(timers.keys());
    for (const k of keys) {
      clearTimer(k);
      pending.delete(k);
    }
  };

  const isPending: TrailingDebounce["isPending"] = (key) => pending.has(key);

  return { schedule, flush, cancel, isPending };
}
